import { inject, Injectable, signal } from "@angular/core";
import { AccessLevel, AccessLevels } from "../../../pojo/Fls";
import { ContactService } from "../../../services/contact.service";
import { RoleRights } from "./role.rights";
import { SeSecurityOrgConfigStore } from "./se.security.org.config.store";
import {
    AcctContRightGrant,
    AcctContRightGrantWrite,
    UserScopedAccessConfigService,
} from "./user.scoped.access.config.service";

export type OrgGrantContactSummary = {
    contId: number;
    displayName: string;
    grants: AcctContRightGrant[];
    rightLabels: string[];
};

/**
 * Per-contact individual grants for the current organization ({@code acct_cont_right}).
 * Relies on {@link SeSecurityOrgConfigStore} for baseline definitions and org+role effective levels.
 */
@Injectable()
export class SeSecurityOrgContactGrantsStore {
    private readonly orgStore = inject(SeSecurityOrgConfigStore);

    private orgId: number | null = null;
    private contId: number | null = null;
    private contRoleId: number | null = null;

    readonly definitions = signal<RoleRights.RightWithRolesConfig[]>([]);
    readonly individualGrants = signal<Map<string, AccessLevel>>(new Map());

    readonly orgGrantIndex = signal<Map<number, AcctContRightGrant[]>>(new Map());
    readonly orgGrantContactNames = signal<Map<number, string>>(new Map());

    readonly loading = signal(false);
    readonly orgGrantIndexLoading = signal(false);
    readonly saving = signal(false);
    readonly error = signal<string | null>(null);
    readonly orgGrantIndexError = signal<string | null>(null);

    individualLevel(rightName: string): AccessLevel {
        return this.individualGrants().get(rightName) ?? AccessLevels.None;
    }

    orgGrantSummaries(rightDefinitions: RoleRights.RightWithRolesConfig[]): OrgGrantContactSummary[] {
        const rightLabelByName = new Map(rightDefinitions.map((def) => [def.rightName, def.displayName]));
        return [...this.orgGrantIndex().entries()]
            .filter(([contId, grants]) => !!this.orgGrantContactNames().get(contId))
            .map(([contId, grants]) => ({
                contId,
                displayName: this.orgGrantContactNames().get(contId)!,
                grants,
                rightLabels: grants.map((grant) => rightLabelByName.get(grant.rightName) ?? grant.rightName),
            }))
            .sort((left, right) => left.displayName.localeCompare(right.displayName));
    }

    mergeOrgGrantContactNames(memberNamesByContId: Map<number, string>): boolean {
        if (!memberNamesByContId.size) return false;

        const merged = new Map(this.orgGrantContactNames());
        let changed = false;
        for (const [contactId, name] of memberNamesByContId) {
            const trimmedName = name?.trim();
            if (contactId == null || !trimmedName) continue;
            if (merged.get(contactId) === trimmedName) continue;
            merged.set(contactId, trimmedName);
            changed = true;
        }
        if (!changed) return false;

        this.orgGrantContactNames.set(merged);
        return true;
    }

    rememberContactDisplayName(contactId: number, displayName: string | undefined): void {
        const trimmedName = displayName?.trim();
        if (contactId == null || !trimmedName) return;
        if (this.orgGrantContactNames().get(contactId) === trimmedName) return;

        const merged = new Map(this.orgGrantContactNames());
        merged.set(contactId, trimmedName);
        this.orgGrantContactNames.set(merged);
    }

    setContext(orgId: number, contId: number | null, contRoleId: number | null): void {
        this.orgId = orgId;
        this.contId = contId;
        this.contRoleId = contRoleId;
        if (contId == null) {
            this.definitions.set([]);
            this.individualGrants.set(new Map());
            this.error.set(null);
        }
    }

    setOrgContext(orgId: number): void {
        this.orgId = orgId;
    }

    clear(): void {
        this.orgId = null;
        this.contId = null;
        this.contRoleId = null;
        this.definitions.set([]);
        this.individualGrants.set(new Map());
        this.orgGrantIndex.set(new Map());
        this.orgGrantContactNames.set(new Map());
        this.error.set(null);
        this.orgGrantIndexError.set(null);
    }

    clearSelectedContact(): void {
        this.contId = null;
        this.contRoleId = null;
        this.definitions.set([]);
        this.individualGrants.set(new Map());
        this.error.set(null);
    }

    async loadOrgGrantIndex(memberNamesByContId?: Map<number, string>): Promise<void> {
        if (this.orgId == null) {
            this.orgGrantIndex.set(new Map());
            this.orgGrantContactNames.set(new Map());
            return;
        }

        this.orgGrantIndexLoading.set(true);
        this.orgGrantIndexError.set(null);
        try {
            const rows = await UserScopedAccessConfigService.getOrgIndividualGrants(this.orgId);
            const grouped = new Map<number, AcctContRightGrant[]>();
            for (const grant of rows) {
                const contactId = grant.contId;
                if (contactId == null) continue;
                const existing = grouped.get(contactId) ?? [];
                existing.push({ ...grant, contId: contactId });
                grouped.set(contactId, existing);
            }
            this.orgGrantIndex.set(grouped);

            const nameMap = new Map<number, string>();
            for (const [contactId, name] of memberNamesByContId ?? []) {
                SeSecurityOrgContactGrantsStore.setContactName(nameMap, contactId, name);
            }

            const missingIds = [...grouped.keys()].filter((contactId) => !nameMap.has(contactId));
            if (missingIds.length) {
                const contacts = await ContactService.getContactsNameAndFormal(missingIds);
                for (const contact of contacts) {
                    SeSecurityOrgContactGrantsStore.setContactName(nameMap, contact.itemId, contact.itemName);
                }
            }
            this.orgGrantContactNames.set(nameMap);
        } catch (caughtError) {
            console.error("SeSecurityOrgContactGrantsStore.loadOrgGrantIndex", caughtError);
            this.orgGrantIndexError.set("Failed to load users with individual grants.");
            this.orgGrantIndex.set(new Map());
            this.orgGrantContactNames.set(new Map());
        } finally {
            this.orgGrantIndexLoading.set(false);
        }
    }

    async load(): Promise<void> {
        if (this.orgId == null || this.contId == null) {
            this.definitions.set([]);
            this.individualGrants.set(new Map());
            return;
        }

        this.loading.set(true);
        this.error.set(null);
        try {
            this.definitions.set(this.orgStore.getBaselineRightDefinitions());
            const rows = await UserScopedAccessConfigService.getContactOrgGrants(this.contId, this.orgId);
            const map = new Map<string, AccessLevel>();
            for (const grant of rows) {
                map.set(grant.rightName, grant.accessLevel as AccessLevel);
            }
            this.individualGrants.set(map);
        } catch (caughtError) {
            console.error("SeSecurityOrgContactGrantsStore.load", caughtError);
            this.error.set("Failed to load contact-specific rights.");
            this.individualGrants.set(new Map());
        } finally {
            this.loading.set(false);
        }
    }

    async setIndividualLevel(rightName: string, level: AccessLevel): Promise<void> {
        if (this.orgId == null || this.contId == null) return;

        const prev = this.individualLevel(rightName);
        const map = new Map(this.individualGrants());

        this.saving.set(true);
        this.error.set(null);
        try {
            if (level === AccessLevels.None) {
                if (prev !== AccessLevels.None) {
                    await UserScopedAccessConfigService.deleteContactOrgGrant(this.contId, this.orgId, rightName);
                    map.delete(rightName);
                }
            } else {
                const row: AcctContRightGrantWrite = {
                    contId: this.contId,
                    orgId: this.orgId,
                    rightName,
                    accessLevel: level,
                };
                await UserScopedAccessConfigService.putContactOrgGrants([row]);
                map.set(rightName, level);
            }
            this.individualGrants.set(map);
            await this.loadOrgGrantIndex(this.orgGrantContactNames());
        } catch (caughtError) {
            console.error("SeSecurityOrgContactGrantsStore.setIndividualLevel", caughtError);
            this.error.set("Failed to save contact-specific right.");
        } finally {
            this.saving.set(false);
        }
    }

    async removeAllGrantsForContact(contId: number, memberNamesByContId?: Map<number, string>): Promise<void> {
        if (this.orgId == null) return;

        this.saving.set(true);
        this.error.set(null);
        this.orgGrantIndexError.set(null);
        try {
            await UserScopedAccessConfigService.deleteAllContactOrgGrants(contId, this.orgId);
            if (this.contId === contId) {
                this.individualGrants.set(new Map());
            }
            await this.loadOrgGrantIndex(memberNamesByContId);
        } catch (caughtError) {
            console.error("SeSecurityOrgContactGrantsStore.removeAllGrantsForContact", caughtError);
            this.orgGrantIndexError.set("Failed to remove individual grants.");
        } finally {
            this.saving.set(false);
        }
    }

    /** Returns true when a display name was stored. */
    private static setContactName(
        nameMap: Map<number, string>,
        contactId: number,
        displayName: string | undefined,
    ): boolean {
        const trimmedName = displayName?.trim();
        if (!trimmedName) return false;
        nameMap.set(contactId, trimmedName);
        return true;
    }
}
